import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    createOrgTenant,
    fetchOrgTenants,
    OrgAdminApiError,
    updateOrgTenant,
    type Organization,
    type OrgTenant,
    type OrgTenantCreated,
} from '@/api/orgAdmin';
import {
    Button,
    DataTable,
    DropdownMenu,
    EmptyState,
    ErrorState,
    FormField,
    Input,
    Modal,
    SectionCard,
    SkeletonTable,
    StatusTag,
    useToast,
    type ColumnConfig,
    type DropdownMenuItem,
    type StatusConfig,
} from '@/components/ui';
import styles from './OrgDetail.module.css';

interface OrgTenantsTabProps {
    org: Organization;
    canManage: boolean;
    /** `?action=create` — the "Thêm cơ sở" affordance from the list's amber
     * "Chưa có cơ sở" state and the quick-peek drawer land here already open. */
    autoOpenCreate: boolean;
    onAutoOpenConsumed: () => void;
}

/**
 * EAA-1815: `/organizations/:id?tab=tenants` — full cơ sở management.
 *
 * Logic carried over from the pre-redesign `OrganizationDetailPage` almost
 * verbatim (create/deactivate/reactivate, the one-time API-token banner);
 * only the surrounding chrome changed. "Cấp cha" always reads the org's own
 * name — see `OrgOverviewTab`'s doc comment for why no real nested
 * tenant-under-tenant data exists to show here.
 */
export function OrgTenantsTab({
    org,
    canManage,
    autoOpenCreate,
    onAutoOpenConsumed,
}: OrgTenantsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const orgId = org.id;

    const [tenantForm, setTenantForm] = useState({ name: '', slug: '', admin_email: '' });
    const [tenantModalOpen, setTenantModalOpen] = useState(false);
    const [tenantFormError, setTenantFormError] = useState<string | null>(null);
    const [pendingDeactivate, setPendingDeactivate] = useState<OrgTenant | null>(null);
    const [toggleError, setToggleError] = useState<string | null>(null);
    const [createdTenant, setCreatedTenant] = useState<OrgTenantCreated | null>(null);

    useEffect(() => {
        if (autoOpenCreate) {
            setTenantModalOpen(true);
            onAutoOpenConsumed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpenCreate]);

    const tenantsQuery = useQuery({
        // EAA-1861: one key shape for org tenants everywhere — `['org-tenants', orgId, token]` — so
        // the admin list peek, the detail page, this tab and the console hierarchy share one
        // cache entry and one invalidation prefix instead of refetching the same rows per page.
        queryKey: ['org-tenants', orgId, accessToken],
        queryFn: () => fetchOrgTenants(config.apiBase, accessToken as string, orgId),
        retry: false,
    });
    const tenants = tenantsQuery.data ?? [];

    const apiErrorMessage = (error: unknown, fallback: string) =>
        error instanceof OrgAdminApiError ? error.message : fallback;

    const createTenantMutation = useMutation({
        mutationFn: () =>
            createOrgTenant(config.apiBase, accessToken as string, orgId, {
                name: tenantForm.name.trim(),
                slug: tenantForm.slug.trim(),
                ...(tenantForm.admin_email.trim()
                    ? { admin_email: tenantForm.admin_email.trim() }
                    : {}),
            }),
        onSuccess: (data) => {
            setCreatedTenant(data);
            setTenantForm({ name: '', slug: '', admin_email: '' });
            setTenantFormError(null);
            setTenantModalOpen(false);
            toast.success(
                t('admin.organizations.tenantsTab.toast.created', {
                    name: data.tenant.name ?? data.tenant.slug,
                })
            );
            void queryClient.invalidateQueries({ queryKey: ['org-tenants', orgId] });
        },
        onError: (error) => {
            setTenantFormError(
                apiErrorMessage(error, t('admin.organizations.tenantsTab.form.genericCreateError'))
            );
        },
    });

    const toggleTenantMutation = useMutation({
        mutationFn: (args: { tenantId: string; is_active: boolean }) =>
            updateOrgTenant(config.apiBase, accessToken as string, orgId, args.tenantId, {
                is_active: args.is_active,
            }),
        onSuccess: (_data, variables) => {
            setToggleError(null);
            setPendingDeactivate(null);
            toast.success(
                variables.is_active
                    ? t('admin.organizations.tenantsTab.toast.reactivated')
                    : t('admin.organizations.tenantsTab.toast.deactivated')
            );
            void queryClient.invalidateQueries({ queryKey: ['org-tenants', orgId] });
        },
        onError: (error) => {
            setToggleError(
                apiErrorMessage(error, t('admin.organizations.tenantsTab.genericUpdateError'))
            );
        },
    });

    const onCreateTenant = (event: FormEvent) => {
        event.preventDefault();
        if (!tenantForm.name.trim() || !tenantForm.slug.trim()) {
            setTenantFormError(t('admin.organizations.tenantsTab.form.validationError'));
            return;
        }
        createTenantMutation.mutate();
    };

    const openTenantModal = () => {
        setTenantFormError(null);
        setTenantModalOpen(true);
    };
    const closeTenantModal = () => {
        if (createTenantMutation.isPending) return;
        setTenantModalOpen(false);
    };
    const closeDeactivateConfirm = () => {
        if (toggleTenantMutation.isPending) return;
        setPendingDeactivate(null);
        setToggleError(null);
    };

    function tenantRowMenuItems(tenant: OrgTenant): DropdownMenuItem[] {
        return tenant.tenant_is_active
            ? [
                  {
                      key: 'deactivate-tenant',
                      label: t('admin.organizations.deactivateAction'),
                      danger: true,
                      onSelect: () => setPendingDeactivate(tenant),
                  },
              ]
            : [
                  {
                      key: 'reactivate-tenant',
                      label: t('admin.organizations.reactivateAction'),
                      onSelect: () =>
                          toggleTenantMutation.mutate({
                              tenantId: tenant.tenant_id,
                              is_active: true,
                          }),
                  },
              ];
    }

    // EAA-1804: `tenantStatusConfig` mirrors `orgFormat.getOrgStatusConfig`'s
    // active/inactive tones (this list has no "no_unit"/"no_tenant" state of
    // its own).
    const tenantColumns: ColumnConfig<OrgTenant>[] = useMemo(() => {
        const tenantStatusConfig: Record<string, StatusConfig> = {
            active: { label: t('admin.organizations.status.active'), tone: 'success' },
            inactive: { label: t('admin.organizations.status.inactive'), tone: 'default' },
        };
        return [
            {
                key: 'name',
                title: t('admin.organizations.columnName'),
                dataIndex: 'name',
                render: (_value, tenant) => tenant.name ?? tenant.tenant_id,
            },
            {
                key: 'parent',
                title: t('admin.organizations.columnParentLevel'),
                render: () => <span style={{ color: 'var(--color-text-muted)' }}>{org.name}</span>,
            },
            {
                key: 'slug',
                title: t('admin.organizations.columnSlug'),
                dataIndex: 'slug',
                render: (_value, tenant) => <code>{tenant.slug}</code>,
            },
            {
                key: 'status',
                title: t('admin.organizations.columnStatus'),
                render: (_value, tenant) => (
                    <StatusTag
                        status={tenant.tenant_is_active ? 'active' : 'inactive'}
                        config={tenantStatusConfig}
                    />
                ),
            },
        ];
    }, [t, org.name]);

    return (
        <div className={styles.main}>
            <SectionCard
                title={t('admin.organizations.tenantsTab.sectionTitle', {
                    count: tenants.length,
                })}
                extra={
                    // EAA-1804: kit `Button` instead of hand-written
                    // `<button className="portal-btn portal-btn-primary">` (both here and the
                    // matching empty-state action below).
                    canManage && (
                        <Button type="primary" onClick={openTenantModal}>
                            {t('admin.organizations.tenantsTab.createButton')}
                        </Button>
                    )
                }
            >
                {toggleError && <p className="portal-admin-error">{toggleError}</p>}
                {tenantsQuery.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingSrOnly')}
                        </span>
                        <SkeletonTable rows={3} columns={4} />
                    </div>
                )}
                {tenantsQuery.isError && (
                    <ErrorState
                        description={t('admin.organizations.tenantsTab.loadErrorDescription')}
                        onRetry={() => void tenantsQuery.refetch()}
                        retrying={tenantsQuery.isFetching}
                    />
                )}
                {tenantsQuery.isSuccess && tenants.length === 0 && (
                    <EmptyState
                        title={t('admin.organizations.tenantsTab.emptyTitle')}
                        description={t('admin.organizations.tenantsTab.emptyDescription')}
                        action={
                            canManage && (
                                <Button type="primary" onClick={openTenantModal}>
                                    {t('admin.organizations.createTenantAction')}
                                </Button>
                            )
                        }
                    />
                )}
                {/* EAA-1804: hand-rolled `<table className="portal-admin-table">` replaced
                    with kit `DataTable` — row-menu shape follows `AppsTable.tsx`'s `actions`
                    column (two states: deactivate when active, reactivate when not). */}
                {tenants.length > 0 && (
                    <div className="portal-table-scroll">
                        <DataTable<OrgTenant>
                            data={tenants}
                            rowKey="id"
                            pagination={false}
                            columns={tenantColumns}
                            actions={
                                canManage
                                    ? (tenant) => (
                                          <DropdownMenu
                                              items={tenantRowMenuItems(tenant)}
                                              triggerAriaLabel={t(
                                                  'admin.organizations.tenantsTab.rowActionsAriaLabel',
                                                  { name: tenant.name ?? tenant.tenant_id }
                                              )}
                                          />
                                      )
                                    : undefined
                            }
                        />
                    </div>
                )}

                {createdTenant?.api_token?.key && (
                    <div className="portal-admin-secret" role="alert">
                        <strong>{t('admin.organizations.tenantsTab.apiTokenBannerTitle')}</strong>
                        <code>{createdTenant.api_token.key}</code>
                        {/* EAA-1804: bare `<button>` that inherited the portal-btn skin only via
                            the legacy `.portal-admin-secret button:not(.ant-btn)` selector
                            (01-controls.css). Kit `Button` keeps the same look. */}
                        <Button onClick={() => setCreatedTenant(null)}>
                            {t('admin.organizations.tenantsTab.apiTokenSavedButton')}
                        </Button>
                    </div>
                )}
            </SectionCard>

            {canManage && (
                <Modal
                    open={tenantModalOpen}
                    onCancel={closeTenantModal}
                    title={t('admin.organizations.tenantsTab.createModal.title')}
                    footer={
                        <>
                            <Button onClick={closeTenantModal} disabled={createTenantMutation.isPending}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form="create-tenant-form"
                                disabled={createTenantMutation.isPending}
                            >
                                {createTenantMutation.isPending
                                    ? t('admin.organizations.tenantsTab.createModal.submitPending')
                                    : t('admin.organizations.createTenantAction')}
                            </Button>
                        </>
                    }
                >
                    {/* EAA-1804: hand-rolled `<label>` field chrome → kit `FormField`/`Input`
                        (4 fields, including the disabled "Cấp cha" field that always shows the
                        parent org's own name — see the tab's doc comment for why). */}
                    <form
                        id="create-tenant-form"
                        className="portal-admin-form"
                        onSubmit={onCreateTenant}
                    >
                        <FormField label={t('admin.organizations.columnParentLevel')}>
                            <Input value={org.name} disabled />
                        </FormField>
                        <FormField label={t('admin.organizations.tenantsTab.form.nameLabel')}>
                            <Input
                                value={tenantForm.name}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, name: e.target.value })
                                }
                                placeholder={t('admin.organizations.tenantsTab.form.namePlaceholder')}
                                required
                            />
                        </FormField>
                        <FormField label={t('admin.organizations.columnSlug')}>
                            <Input
                                value={tenantForm.slug}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, slug: e.target.value })
                                }
                                placeholder={t('admin.organizations.tenantsTab.form.slugPlaceholder')}
                                required
                            />
                        </FormField>
                        <FormField label={t('admin.organizations.tenantsTab.form.adminEmailLabel')}>
                            <Input
                                type="email"
                                value={tenantForm.admin_email}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, admin_email: e.target.value })
                                }
                                placeholder={t(
                                    'admin.organizations.tenantsTab.form.adminEmailPlaceholder'
                                )}
                            />
                        </FormField>
                        {tenantFormError && (
                            <p className="portal-admin-error">{tenantFormError}</p>
                        )}
                    </form>
                </Modal>
            )}

            <Modal
                open={pendingDeactivate !== null}
                onCancel={closeDeactivateConfirm}
                title={t('admin.organizations.tenantsTab.deactivateModal.title')}
                width={384}
                footer={
                    <>
                        <Button
                            onClick={closeDeactivateConfirm}
                            disabled={toggleTenantMutation.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            danger
                            disabled={toggleTenantMutation.isPending}
                            onClick={() => {
                                if (!pendingDeactivate) return;
                                toggleTenantMutation.mutate({
                                    tenantId: pendingDeactivate.tenant_id,
                                    is_active: false,
                                });
                            }}
                        >
                            {toggleTenantMutation.isPending
                                ? t('admin.organizations.processingLabel')
                                : t('admin.organizations.deactivateAction')}
                        </Button>
                    </>
                }
            >
                <p>
                    {t('admin.organizations.tenantsTab.deactivateModal.bodyPrefix')}{' '}
                    <strong>{pendingDeactivate?.name ?? pendingDeactivate?.tenant_id}</strong>{' '}
                    {t('admin.organizations.tenantsTab.deactivateModal.bodySuffix')}
                </p>
            </Modal>
        </div>
    );
}

export default OrgTenantsTab;
